import { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  FormControl,
  OutlinedInput,
  MenuItem,
  Select,
  InputLabel,
  alpha,
} from '@mui/material';
import useTenantQuery from '@/hooks/useTenantQuery';
import SecurityIcon from '@mui/icons-material/Security';
import FormButtonControls from '@/components/forms/formButtonControls/FormButtonControls';

const AdminRoleForm = ({
  app,
  next,
  back,
  isLastStep,
  isFirstStep,
  handleCancel,
}) => {
  const { data: roles } = useTenantQuery('role');
  const [selectedRoles, setselectedRoles] = useState([]);
  const [error, setError] = useState(true);

  const handleUserChange = (event) => {
    setselectedRoles(event.target.value);
  };

  const handleRemoveUser = (userId) => {
    setselectedRoles((prevSelected) =>
      prevSelected.filter((id) => id !== userId),
    );
  };

  const handleSave = () => {
    if (selectedRoles.length === 0) {
      setError(true);
    } else {
      setError(false);
      next({ adminRoles: selectedRoles });
    }
  };

  useEffect(() => {
    if (selectedRoles.length === 0) {
      setError(true);
    } else {
      setError(false);
    }
  }, [selectedRoles.length]);

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: '20%',
      },
    },
  };

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <CardHeader
        avatar={<SecurityIcon color="primary" />}
        title={<Typography variant="h6">Admin-Rollenzuweisung</Typography>}
        subheader={`Für ${app.title}`}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.light, 0.1),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <CardContent sx={{ pt: 3, pb: 2, flex: 1 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Wählen Sie die Adminrollen aus, die Zugriff auf {app.title} haben
          sollen.
        </Typography>

        <FormControl fullWidth variant="outlined">
          <InputLabel id="user-select-label">AdminRolle</InputLabel>
          <Select
            labelId="user-select-label"
            id="user-select"
            multiple
            value={selectedRoles}
            onChange={handleUserChange}
            MenuProps={MenuProps}
            input={
              <OutlinedInput label="AdminRolle" id="select-multiple-chip" />
            }
          >
            {roles.map((role) => (
              <MenuItem key={role._id} value={role._id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedRoles.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Ausgewählte Rollen:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mt: 1,
                p: 2,
                borderRadius: 1,
                bgcolor: (theme) =>
                  alpha(theme.palette.background.default, 0.7),
              }}
            >
              {selectedRoles.map((roleId) => {
                const role = roles.find((r) => r._id === roleId);
                if (!role) return null;
                return (
                  <Chip
                    key={role._id}
                    label={role.name}
                    onDelete={() => handleRemoveUser(roleId)}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {error && selectedRoles.length === 0 && (
          <Typography
            color="error"
            variant="caption"
            sx={{ display: 'block', mt: 1 }}
          >
            Bitte wählen Sie mindestens eine Rolle aus
          </Typography>
        )}
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
        <FormButtonControls
          next={next && handleSave}
          back={back}
          cancel={handleCancel}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          disabled={error}
        />
      </CardActions>
    </Card>
  );
};

export default AdminRoleForm;
